import { memo, type CSSProperties } from 'react';
import type { ObjectId, SpatialObject } from '@/types';
import { useObject } from '@/hooks/useObject';
import { useSelectionStore } from '@/store/selectionStore';
import { useInteractionStore } from '@/store/interactionStore';
import { useObjectWorldMatrix } from '@/hooks/useObjectWorldMatrix';
import { rendererFor, type AnyRenderer } from './renderers/registry';
import styles from './ObjectView.module.css';

/**
 * Every object is a DOM sibling in the ObjectLayer (no nesting), so the frame carries
 * the full world matrix: parent chain · translate · rotate · scale about the centre.
 */
const frameStyle = (obj: SpatialObject, worldMatrix: string): CSSProperties => ({
  transform: worldMatrix,
  width: obj.size.width,
  height: obj.size.height,
  zIndex: obj.zIndex,
  display: obj.visible ? undefined : 'none',
});

/**
 * Positions an object in world space and delegates its content to the renderer
 * registered for its kind. Memoised: it re-renders only when its own object or its
 * selected state changes.
 */
export const ObjectView = memo(({ id }: { id: ObjectId }) => {
  const obj = useObject(id);
  const worldMatrix = useObjectWorldMatrix(id);
  const selected = useSelectionStore((s) => s.selection.objectIds.includes(id));
  const hovered = useInteractionStore((s) => s.hoveredId === id);
  const highlighted = useInteractionStore((s) => s.highlightedId === id);
  if (!obj || !worldMatrix) return null;
  const Renderer = rendererFor(obj.kind) as AnyRenderer;

  return (
    <article
      className={styles.object}
      style={frameStyle(obj, worldMatrix)}
      data-object-id={id}
      data-kind={obj.kind}
      data-selected={selected}
      data-locked={obj.locked || undefined}
      data-collapsed={('collapsed' in obj && obj.collapsed) || undefined}
      data-color={obj.kind === 'note' ? obj.color : undefined}
      data-hovered={hovered || undefined}
      data-highlighted={highlighted || undefined}
      aria-label={obj.name}
    >
      <Renderer object={obj} selected={selected} />
    </article>
  );
});
ObjectView.displayName = 'ObjectView';
